import { DashboardProject, FeatureManagementType, Permissions } from "@api/apiClient";
import { useAppSettings } from "@api/appSettings";
import { useDashboardProjects, useDeleteProject } from "@api/projects";
import CircularProgressWithContent from "@components/CircularProgressWithContent/CircularProgressWithContent";
import DataGrid from "@components/DataGrid/DataGrid";
import useAppStore from "@hooks/useAppStore";
import useUserPermissionStore from "@hooks/useUserPermissionStore";
import i18n from "@i18n/i18n";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, Tooltip, Typography, useTheme } from "@mui/material";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import { GridActionsCellItem, GridCallbackDetails, GridColDef, GridRowParams, MuiEvent, useGridApiRef } from "@mui/x-data-grid-premium";
import { useConfirm } from "material-ui-confirm";
import { observer } from "mobx-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import useResizeObserver from "use-resize-observer";
import AddProjectUser from "./ProjectUser/AddProjectUser";

export interface DashboardGridProps {
  data: DashboardProject[];
  handleRefreshClick: () => void;
}

const autosizeOptions = {
  columns: ["name"],
  includeOutliers: true,
  includeHeaders: true,
  disableColumnVirtualization: true,
  expand: true,
};

const DashboardGrid = observer((props: DashboardGridProps) => {
  const appStore = useAppStore();
  const appSettings = useAppSettings();
  const theme = useTheme();
  const { data } = props;
  const navigate = useNavigate();
  const deleteProject = useDeleteProject();

  const apiRef = useGridApiRef();
  const containerRef = useRef(null);
  const upStore = useUserPermissionStore();

  const [openAddProjectUser, setAddProjectUser] = useState(false);
  const [clickedProject, setClickedProject] = useState<DashboardProject | null>(null);

  const dashboardProjects = useDashboardProjects();
  const confirm = useConfirm();

  useEffect(() => {
    const project = dashboardProjects.data?.find((x) => x.projectId === clickedProject?.projectId);
    if (project) {
      setClickedProject(project);
    }
  }, [dashboardProjects.data]);

  // TODO: replace this with the new useAutosizeColumns hook
  // but need to add resize handler to it
  useResizeObserver({
    ref: containerRef,
    onResize: () => {
      setTimeout(() => {
        apiRef.current?.autosizeColumns(autosizeOptions);
      }, 100);
    },
  });
  // TODO: replace this with the new useAutosizeColumns hook
  // but need to add resize handler to it

  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: i18n.t("dashboard.title.project-name"),
      flex: 1,
      renderCell: (params) => {
        return (
          <Box sx={{ display: "inline-flex", alignItems: "center", justifyContent: "start" }}>
            {params.row.imageUrl && <Box ml={1} component="img" src={params.row.imageUrl} width="36px" height="36px" sx={{ objectFit: "cover" }} />}
            {!params.row.imageUrl && <Box ml={1} component="img" src="/img/img-placeholder-1x.png" width="36px" height="36px" alt="" sx={{ objectFit: "contain" }} />}
            <Box sx={{ display: "inline-block" }}>
              <Typography ml={1} variant="subtitle1">
                {params.row.name}
              </Typography>
              <Typography ml={1} variant="body2" sx={{ color: theme.palette.text.secondary }}>
                {params.row.description}
              </Typography>
            </Box>
            <Box ml={2} sx={{ display: "inline-block" }}>
              {appStore.jobStatus.hasActiveJobs(params.row.projectId) && (
                <Tooltip title={i18n.t("dashboard.progress-indicator.title")}>
                  <CircularProgressWithContent size={32} content={<DownloadIcon />} />
                </Tooltip>
              )}
            </Box>
          </Box>
        );
      },
    },
    ...(appStore.hasFeature(FeatureManagementType.ProjectExtendedDetails)
      ? ([
          {
            field: "projectNumber",
            headerName: i18n.t("dashboard.title.project-number"),
            type: "string",
            flex: 1,
          } as GridColDef,
        ] as GridColDef[])
      : []),
    {
      field: "modelCount",
      headerName: i18n.t("dashboard.title.model-count"),
      type: "number",
      flex: 1,
    },
    {
      field: "scenarioCount",
      headerName: i18n.t("dashboard.title.scenario-count"),
      type: "number",
      flex: 1,
    },
    {
      field: "ownerName",
      headerName: i18n.t("dashboard.title.project-owner"),
      flex: 1,
    },
    {
      field: "profileName",
      headerName: i18n.t("dashboard.title.profile-name"),
      type: "string",
      flex: 1,
    },
    {
      field: "createdAt",
      headerName: i18n.t("dashboard.title.created-at"),
      type: "number",
      flex: 1,
      renderCell: (params: any) => {
        return i18n.t("datetime_short", { value: new Date(params.row.createdAt) });
      },
    },
    {
      field: "modifiedAt",
      headerName: i18n.t("dashboard.title.modified-at"),
      type: "number",
      flex: 1,
      renderCell: (params: any) => {
        return i18n.t("datetime_short", { value: new Date(params.row.modifiedAt) });
      },
    },
    {
      field: "actions",
      type: "actions",
      headerName: i18n.t("rebar-grid.columns.actions"),
      resizable: false,
      width: 100,
      getActions: (params: GridRowParams) => {
        const projectPermissions = data.find((item) => item.projectId === params.id)?.permissions || [];
        const clickedProject = data.find((item) => item.projectId === params.id);
        const actions = [];
        if (appStore.hasFeature(FeatureManagementType.ShareProject)) {
          actions.push(
            <GridActionsCellItem
              key={`add-${params.id}`}
              icon={
                <Tooltip title={i18n.t("materials-tab.grid.add-user-button.tooltip")}>
                  <Box sx={{ width: "24px", height: "24px" }}>
                    {!params.row.apsProjectId && <PersonAddAltIcon />}
                    {params.row.apsProjectId && (
                      <>
                        <PersonOutlineIcon />
                        <CheckCircleIcon sx={{ position: "absolute", top: 8, right: 0, fontSize: "small", color: theme.palette.success.main }} />
                      </>
                    )}
                  </Box>
                </Tooltip>
              }
              label={i18n.t("materials-tab.grid.add-user-button.label")}
              onClick={() => handleAddProjectUserClick(clickedProject as DashboardProject)}
              disabled={!upStore.hasPermissionForDashBoard(Permissions.App_Project_External_Link_Add_Users, projectPermissions)}
            />,
          );
        }

        // Always add the 'delete' action
        actions.push(
          <GridActionsCellItem
            key={`delete-${params.id}`}
            icon={
              <Tooltip title={i18n.t("dashboard.delete-button.title")}>
                <DeleteIcon />
              </Tooltip>
            }
            label={i18n.t("dashboard.delete-button.title")}
            onClick={() => handleDeleteConfirm(params.id as string)}
            disabled={!upStore.hasPermissionForDashBoard(Permissions.App_Project_Delete, projectPermissions)}
          />,
        );

        return actions;
      },
    },
  ];

  const handleDeleteConfirm = async (projectId: string) => {
    const { confirmed } = await confirm({
      title: i18n.t("dashboard.delete-dialog.header"),
      description: i18n.t("dashboard.delete-dialog.title"),
      confirmationText: i18n.t("dialog.yes"),
      cancellationText: i18n.t("dialog.cancel"),
      dialogProps: {
        maxWidth: "xs",
      },
      confirmationButtonProps: {
        variant: "contained",
        color: "error",
      },
      cancellationButtonProps: {
        variant: "outlined",
      },
    });
    if (confirmed) {
      await deleteProject.mutateAsync(projectId as any);
    }
  };

  function handleAddProjectUserClick(project: DashboardProject) {
    setClickedProject(project);
    setAddProjectUser(true);
  }

  const handleCloseAddProjectUser = () => {
    setAddProjectUser(false);
  };

  const handleRefreshClick = async () => {
    try {
      appStore.setIsLoading(true);
      await dashboardProjects.refetch();

      props.handleRefreshClick();
    } finally {
      appStore.setIsLoading(false);
    }
  };

  return (
    <Card sx={{ width: "100%" }}>
      <Box ref={containerRef}>
        <DataGrid
          apiRef={apiRef}
          loading={dashboardProjects.isFetching}
          rows={data}
          columns={columns}
          hideFooter={true}
          autoHeight={true}
          initialState={{
            sorting: {
              sortModel: [{ field: "createdAt", sort: "desc" }],
            },
          }}
          getRowId={(row) => row.projectId}
          onRowClick={(params: GridRowParams, _event: MuiEvent, _details: GridCallbackDetails) => {
            navigate(`/projects/${params.id}`);
          }}
          autosizeOptions={autosizeOptions}
          autosizeOnMount
        />
        <Dialog
          fullWidth={true}
          maxWidth={"lg"}
          open={openAddProjectUser}
          onClose={handleCloseAddProjectUser}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          sx={{
            "& .MuiDialog-paper": {
              height: "95%", // Adjust the height as needed
              maxHeight: "95vh", // Set a maximum height (optional)
            },
          }}
        >
          <DialogTitle
            id="alert-dialog-title"
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            {i18n.t("dashboard-add-user.title")}
            <IconButton title={i18n.t("dashboard-add-user.close-button-tooltip")} onClick={handleCloseAddProjectUser}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description"></DialogContentText>
            <AddProjectUser
              projectId={clickedProject?.projectId || ""}
              projectName={clickedProject?.name || ""}
              apsHubId={clickedProject?.apsHubId || ""}
              apsProjectId={clickedProject?.apsProjectId || ""}
              handleRefreshClick={handleRefreshClick}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseAddProjectUser} color="primary" variant="contained">
              {i18n.t("dashboard-add-user.close-button")}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Card>
  );
});

export default DashboardGrid;
